import { primeLeadToken, submitLead } from './submit-lead.js';
import { trackEvent } from './analytics.js';

export function connectQuestionForms() {
    const form = document.querySelector('.contact-form--js');
    if (!form) {
        console.info('[CPB:lead] No .contact-form--js found, skipping question form wiring');
        return;
    }

    let inFlight = false;
    let successOpen = false;

    primeLeadToken(form, 'site-contact');
    form.addEventListener('submit', handleSubmit);

    function handleSubmit(e) {
        e.preventDefault();

        if (inFlight) {
            console.info('[CPB:lead] Question form submit already in flight, ignoring duplicate');
            return false;
        }

        inFlight = true;
        setSubmitDisabled(true);

        // Read defensively: a throw here would happen after inFlight is set and the button
        // disabled, leaving the form permanently dead with no promise chain to recover it.
        submitLead('site-contact', {
            name: form.elements.name?.value || '',
            email: form.elements.email?.value || '',
            categories: form.elements.categories?.value || '',
            message: form.elements.message?.value || '',
            website: form.elements.website?.value || '',
        }, form)
            .then(() => {
                console.info('[CPB:lead] Question form submitted');
                trackEvent('form_submit', { event_category: 'lead', form_type: 'contact' });
                showSuccess();
            })
            .catch((err) => {
                console.error('[CPB:lead] Question form submit failed', err);
                showErrorModal();
            })
            .finally(() => {
                inFlight = false;
                setSubmitDisabled(false);
            });

        return false;
    }

    function setSubmitDisabled(disabled) {
        const button = form.querySelector('button[type="submit"]');
        if (!button) return;
        if (disabled) button.setAttribute('disabled', true);
        else button.removeAttribute('disabled');
    }

    function showSuccess() {
        if (successOpen) return;

        successOpen = true;
        resetContactForm(form);
        showSuccessModal(() => {
            successOpen = false;
        });
    }
}

function showSuccessModal(onClose) {
    // Swal comes from a CDN. Without this guard a blocked script throws inside the success
    // handler, which routes into .catch() and shows the error modal for a lead that landed.
    if (typeof Swal === 'undefined') {
        if (onClose) onClose();
        return;
    }

    Swal.fire({
        html: `
            <section class="success-modal">
                <div class="success-modal__inner">
                    <div class="success-modal__heading">
                        <h2 class="success-modal__title">THANK YOU FOR YOUR REQUEST</h2>
                        <p class="success-modal__subtitle">we will get back to you shortly</p>
                    </div>
                    <div class="cta">
                        <a href="https://apps.shopify.com/customization-tool?utm_source=website&utm_medium=contact-form" class="button button--bright">TRY FOR FREE</a>
                    </div>
                </div>
            </section>`,
        closeButtonHtml: `
            <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    d="M6 6L18 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M18 6L6 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>`,
        width: '700px',
        showClass: {
            popup: 'modal-popup',
        },
        hideClass: {
            popup: 'modal-popup-out',
        },
        customClass: {
            popup: 'success-modal-popup',
            htmlContainer: 'success-modal-html',
            closeButton: 'modal-close-btn success-modal-close',
        },
        showConfirmButton: false,
        showCloseButton: true,
        didClose: () => {
            // allow future submits
            if (onClose) onClose();
        },
    });
}

function showErrorModal() {
    if (typeof Swal === 'undefined') {
        // The submit failed and the visitor has to be told, so fall back to the plainest thing.
        window.alert('Something went wrong — please try again in a minute or email support@buildateam.io. Your message is still in the form.');
        return;
    }

    Swal.fire({
        html: `
            <section class="success-modal">
                <div class="success-modal__inner">
                    <div class="success-modal__heading">
                        <h2 class="success-modal__title">SOMETHING WENT WRONG</h2>
                        <p class="success-modal__subtitle">please try again in a minute or email us at support@buildateam.io — your message is still in the form</p>
                    </div>
                </div>
            </section>`,
        width: '700px',
        showClass: {
            popup: 'modal-popup',
        },
        hideClass: {
            popup: 'modal-popup-out',
        },
        customClass: {
            popup: 'success-modal-popup',
            htmlContainer: 'success-modal-html',
            closeButton: 'modal-close-btn success-modal-close',
        },
        showConfirmButton: false,
        showCloseButton: true,
    });
}

function resetContactForm(form) {
    if (!form) return;

    // Native inputs (resets to initial values, good for hidden keys)
    form.reset();

    // Explicitly clear controlled visible fields (safety and event triggering)
    const inputs = form.querySelectorAll('input:not([type="hidden"]), textarea');
    inputs.forEach((el) => {
        el.value = '';
        el.dispatchEvent(new Event('input', { bubbles: true }));
    });
}
